import { MonitoringAlert } from '@atoms/Monitoring/types';
import { QueryKeys } from '@common/queries/common';
import { Private_Routes, Public_Routes } from '@common/url';
import { ErrorPageLayout } from '@pages/Errors';
import { Spinner } from '@prophecy/ui-v3';
import { pluralVerb } from '@prophecy/utils/string';
import { capitalize, uniq } from 'lodash-es';
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { useUnsecuredGlobalVariables } from '../../context/globalConfig/unsecuredConfigHook';
import { getMonitoringAlertInfo } from '../../data/apis/api';
import { useRestQuery } from '../../data/util';
import { useDownServiceStatus } from './util';

export default function ServiceDown({ isPublic }: { isPublic?: boolean }) {
  const data = useDownServiceStatus();
  const isProphecyDown = data?.isProphecyDown;
  const isSaasSetup = useUnsecuredGlobalVariables().isSaasSetup;
  const navigate = useNavigate();
  const { data: monitoringData, isInitialLoading: isLoading } = useRestQuery<MonitoringAlert>(
    [QueryKeys.MonitoringAlert],
    getMonitoringAlertInfo,
    {
      refetchInterval: 5 * 60 * 1000,
      // on error do nothing as this API may also be down
      onError: () => {}
    }
  );
  const suspendedServices = uniq(
    monitoringData?.data.critical
      ?.filter((service) => service.suspensionCountDownInSeconds && service.suspensionCountDownInSeconds <= 0)
      .map((service) => service.serviceName)
  );
  useEffect(() => {
    if (isProphecyDown !== undefined && !isProphecyDown) {
      navigate(isPublic ? Public_Routes.SignIn.getUrl(undefined) : Private_Routes.Home.getUrl());
      window.location.reload();
    }
  }, [isProphecyDown, navigate, isPublic]);

  let heading = '';
  let subheading = 'Prophecy \ntemporarily down';
  let description: React.ReactNode =
    'We’re currently experiencing a service outage. Our team is actively working on the issue. We apologize for any inconvenience. Please check again in a few minutes.';

  if (isSaasSetup) {
    description = (
      <>
        <span>You can check our </span>
        <a
          target='_blank'
          href='https://status.prophecy.io/'
          rel='noreferrer'
          data-test-id='service-down-status-page-link'>
          status page
        </a>{' '}
        or contact Prophecy Support for assistance.
      </>
    );
  } else if (suspendedServices.length > 0) {
    description = (
      <Spinner spinning={isLoading && !isSaasSetup}>
        {`${suspendedServices.map(capitalize).join(', ')} ${pluralVerb(suspendedServices.length, 'has', 'have')} reached
        a resource limit and ${pluralVerb(suspendedServices.length, 'has', 'have')} been shutdown to protect your data`}
      </Spinner>
    );
  }

  return <ErrorPageLayout heading={heading} subheading={subheading} description={description} isMaintenance={true} />;
}
